// Blackout Dates der Organisation (/workflows/blackout, Runde 10; Close: Einstellungen › Blackout Dates).
// Ein Jahreskalender: Klick auf einen Tag sperrt ihn, Klick auf einen gesperrten Tag öffnet Name, „jedes Jahr“ und Entfernen.
// Die Feiertage der Region (Einstellungen › Organisation) lassen sich mit einem Klick übernehmen („Auto-Select“).
// An einem gesperrten Tag wird kein E-Mail- und SMS-Schritt (auf Wunsch auch keine Aufgabe) fällig; der Schritt wartet
// bis zum nächsten freien Tag. Gesendet wird in keinem Fall von allein.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { useSeitenKopf } from "../daten/seite";
import { heute as heuteDatum } from "../werkzeuge/zeit";
import { useLaden } from "./daten";
import { WOCHENTAGE_KURZ } from "./teil2Hilfen";
import { workflows2Api, type BlackoutTag } from "./teil2Api";
import "./workflows2.css";

const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
/** Feiertage mit festem Datum gelten Jahr für Jahr (Ostern und was davon abhängt wandert und gilt nur für das gewählte Jahr). */
const FESTE_FEIERTAGE = new Set(["Neujahr", "Tag der Arbeit", "Tag der Deutschen Einheit", "1. Weihnachtstag", "2. Weihnachtstag", "Heilige Drei Könige", "Allerheiligen", "Reformationstag", "Mariä Himmelfahrt", "Weltkindertag", "Internationaler Frauentag"]);

const zwei = (n: number) => String(n).padStart(2, "0");
const datum = (j: number, m: number, t: number) => `${j}-${zwei(m)}-${zwei(t)}`;

/** Der Eintrag, der diesen Tag sperrt (genaues Datum, oder jährlich ab dem eingetragenen Jahr), sonst undefined. */
function eintragFuer(tage: BlackoutTag[], tag: string): BlackoutTag | undefined {
  const genau = tage.find((t) => t.datum === tag);
  if (genau) return genau;
  return tage.find((t) => t.jaehrlich && t.datum.slice(5) === tag.slice(5) && Number(tag.slice(0, 4)) >= Number(t.datum.slice(0, 4)));
}

function Monat({ jahr, monat, tage, feiertage, fokus, heute, nurLesen, onTag }: { jahr: number; monat: number; tage: BlackoutTag[]; feiertage: Map<string, string>; fokus: string | null; heute: string; nurLesen: boolean; onTag: (d: string) => void }) {
  const erster = new Date(Date.UTC(jahr, monat, 1));
  const versatz = (erster.getUTCDay() + 6) % 7; // Montag = 0
  const anzahl = new Date(Date.UTC(jahr, monat + 1, 0)).getUTCDate();
  const zellen: (number | null)[] = [...Array(versatz).fill(null), ...Array.from({ length: anzahl }, (_, i) => i + 1)];
  return (
    <section className="wf-monat" aria-label={`${MONATE[monat]} ${jahr}`}>
      <h3 className="wf-monat__name">{MONATE[monat]}</h3>
      <div className="wf-monat__raster" role="grid">
        {WOCHENTAGE_KURZ.map((w) => (
          <span key={w} className="wf-monat__kopf" aria-hidden="true">
            {w.slice(0, 2)}
          </span>
        ))}
        {zellen.map((t, i) => {
          if (t === null) return <span key={`l${i}`} />;
          const d = datum(jahr, monat + 1, t);
          const e = eintragFuer(tage, d);
          const feiertag = feiertage.get(d);
          const wochenende = (versatz + t - 1) % 7 >= 5;
          const klasse = ["wf-tag", e ? "wf-tag--gesperrt" : "", e?.jaehrlich ? "wf-tag--jaehrlich" : "", feiertag ? "wf-tag--feiertag" : "", wochenende ? "wf-tag--ende" : "", d === heute ? "wf-tag--heute" : "", d === fokus ? "wf-tag--fokus" : ""].filter(Boolean).join(" ");
          const titel = [e ? `Gesperrt${e.name ? `: ${e.name}` : ""}${e.jaehrlich ? " (jedes Jahr)" : ""}` : null, feiertag ?? null].filter(Boolean).join(" · ");
          return (
            <button key={d} type="button" className={klasse} data-datum={d} title={titel || undefined} aria-pressed={!!e} aria-label={`${t}. ${MONATE[monat]} ${jahr}${titel ? `, ${titel}` : ""}`} disabled={nurLesen && !e} onClick={() => onTag(d)}>
              {t}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default function BlackoutSeite() {
  useSeitenKopf([{ label: "Workflows", to: "/workflows" }, { label: "Blackout Dates" }]);
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");
  const heute = heuteDatum();
  const [jahr, setJahr] = useState(Number(heute.slice(0, 4)));
  const [tage, setTage] = useState<BlackoutTag[] | null>(null);
  const [gespeichert, setGespeichert] = useState("[]");
  const [fokus, setFokus] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const g = useLaden(() => workflows2Api.blackout(jahr), [jahr]);
  useEffect(() => {
    if (g.daten && tage === null) {
      setTage(g.daten.tage);
      setGespeichert(JSON.stringify(g.daten.tage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.daten]);

  const feiertage = useMemo(() => new Map((g.daten?.feiertage ?? []).map((f) => [f.datum, f.name])), [g.daten]);
  const liste = tage ?? [];
  const geaendert = tage !== null && JSON.stringify(tage) !== gespeichert;
  const fokusEintrag = fokus ? eintragFuer(liste, fokus) : undefined;

  const tagKlick = (d: string) => {
    if (!kannVerwalten && !eintragFuer(liste, d)) return;
    if (!eintragFuer(liste, d)) {
      if (!kannVerwalten) return;
      setTage([...liste, { datum: d, name: feiertage.get(d) ?? "", jaehrlich: false }].sort((a, b) => a.datum.localeCompare(b.datum)));
    }
    setFokus(d);
  };
  const aendern = (eintrag: BlackoutTag, teil: Partial<BlackoutTag>) => setTage(liste.map((t) => (t === eintrag ? { ...t, ...teil } : t)));
  const entfernen = (eintrag: BlackoutTag) => {
    setTage(liste.filter((t) => t !== eintrag));
    setFokus(null);
  };
  const feiertageUebernehmen = () => {
    const neu: BlackoutTag[] = [];
    for (const f of g.daten?.feiertage ?? []) {
      if (eintragFuer([...liste, ...neu], f.datum)) continue;
      neu.push({ datum: f.datum, name: f.name, jaehrlich: FESTE_FEIERTAGE.has(f.name) });
    }
    if (!neu.length) {
      meldung.zeige("info", `Die Feiertage ${jahr} stehen schon alle in der Liste.`);
      return;
    }
    setTage([...liste, ...neu].sort((a, b) => a.datum.localeCompare(b.datum)));
    meldung.ok(`${neu.length} ${neu.length === 1 ? "Feiertag" : "Feiertage"} übernommen. Zum Festhalten speichern.`);
  };
  const speichern = async () => {
    if (tage === null) return;
    setLaeuft(true);
    try {
      const r = await workflows2Api.blackoutSpeichern(tage);
      setTage(r.tage);
      setGespeichert(JSON.stringify(r.tage));
      meldung.ok("Blackout Dates gespeichert.");
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    } finally {
      setLaeuft(false);
    }
  };
  const verwerfen = () => {
    setTage(JSON.parse(gespeichert) as BlackoutTag[]);
    setFokus(null);
  };

  if (g.fehler && !g.daten && tage === null) return <div className="wf-seite wf-seite--breit"><FehlerBox fehler={g.fehler} onNochmal={() => void g.neuLaden()} /></div>;
  if (tage === null) return <div className="wf-seite wf-seite--breit"><Laden /></div>;

  return (
    <div className="wf-seite wf-seite--breit wf-blackout">
      <div className="wf-kopf">
        <Link to="/workflows" className="wf-kopf__zurueck" title="Zu allen Workflows">
          <Icon name="chevron-links" groesse={14} />
          Workflows
        </Link>
        <h1 className="wf-bericht__titel">Blackout Dates</h1>
        <span className="wf-kopf__rechts">
          {geaendert ? <span className="leise wf-kopf__ungespeichert">Nicht gespeichert</span> : null}
          {kannVerwalten ? (
            <>
              <button type="button" className="knopf" disabled={laeuft || !geaendert} onClick={verwerfen}>
                Verwerfen
              </button>
              <button type="button" className="knopf knopf--primaer" disabled={laeuft || !geaendert} onClick={() => void speichern()}>
                Speichern
              </button>
            </>
          ) : null}
        </span>
      </div>

      <p className="leise wf-blackout__text">
        An diesen Tagen wird kein E-Mail- und SMS-Schritt fällig (mit „Auch Aufgaben“ im Sendefenster auch keine Aufgabe). Der Schritt wartet bis zum nächsten freien Tag. Die Mails und SMS bleiben auch dann Entwürfe zur Prüfung.
        Jeder Workflow kann Blackout Dates in seinem Sendefenster abschalten.
        {!kannVerwalten ? " Ändern dürfen Admins und Super User." : ""}
      </p>

      <div className="wf-blackout__leiste">
        <div className="wf-blackout__jahr">
          <button type="button" className="symbolknopf symbolknopf--rand" aria-label="Vorheriges Jahr" onClick={() => setJahr(jahr - 1)}>
            <Icon name="chevron-links" groesse={15} />
          </button>
          <strong className="wf-blackout__jahrzahl" aria-live="polite">{jahr}</strong>
          <button type="button" className="symbolknopf symbolknopf--rand" aria-label="Nächstes Jahr" onClick={() => setJahr(jahr + 1)}>
            <Icon name="chevron-rechts" groesse={15} />
          </button>
          {jahr !== Number(heute.slice(0, 4)) ? (
            <button type="button" className="textknopf" onClick={() => setJahr(Number(heute.slice(0, 4)))}>
              Dieses Jahr
            </button>
          ) : null}
        </div>
        {kannVerwalten ? (
          <button type="button" className="knopf" onClick={feiertageUebernehmen} disabled={!g.daten?.feiertage.length} title="Die gesetzlichen Feiertage der Region der Organisation eintragen">
            <Icon name="kalender" groesse={14} />
            Feiertage {jahr} übernehmen{g.daten?.region ? ` (${g.daten.region})` : ""}
          </button>
        ) : null}
        <span className="wf-blackout__legende leise">
          <span className="wf-legende wf-legende--gesperrt" /> gesperrt <span className="wf-legende wf-legende--feiertag" /> Feiertag <span className="wf-legende wf-legende--jaehrlich" /> jedes Jahr
        </span>
      </div>

      <div className="wf-blackout__haupt">
        <div className="wf-jahr" aria-label={`Kalender ${jahr}`}>
          {MONATE.map((_, m) => (
            <Monat key={m} jahr={jahr} monat={m} tage={liste} feiertage={feiertage} fokus={fokus} heute={heute} nurLesen={!kannVerwalten} onTag={tagKlick} />
          ))}
        </div>

        <aside className="wf-blackout__seite" aria-label="Gesperrte Tage">
          {fokus && fokusEintrag ? (
            <div className="wf-blackout__karte">
              <div className="wf-blackout__karte-kopf">
                <strong>{fokus.slice(8, 10)}.{fokus.slice(5, 7)}.{fokus.slice(0, 4)}</strong>
                <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Schließen" onClick={() => setFokus(null)}>
                  <Icon name="schliessen" groesse={12} />
                </button>
              </div>
              <label className="feld wf-feld">
                <span className="feld__label">Name</span>
                <input className="eingabe" value={fokusEintrag.name} maxLength={80} placeholder="z. B. Betriebsferien" disabled={!kannVerwalten} onChange={(e) => aendern(fokusEintrag, { name: e.target.value })} />
              </label>
              <label className="wf-haken">
                <input type="checkbox" checked={fokusEintrag.jaehrlich} disabled={!kannVerwalten} onChange={(e) => aendern(fokusEintrag, { jaehrlich: e.target.checked })} />
                <span>Jedes Jahr wiederholen</span>
              </label>
              {kannVerwalten ? (
                <button type="button" className="knopf knopf--gefahr" onClick={() => entfernen(fokusEintrag)}>
                  <Icon name="papierkorb" groesse={14} />
                  Tag freigeben
                </button>
              ) : null}
            </div>
          ) : (
            <p className="leise wf-blackout__tipp">{kannVerwalten ? "Tag im Kalender anklicken, um ihn zu sperren." : "Gesperrte Tage der Organisation."}</p>
          )}

          <h2 className="wf-blackout__titel">Alle Blackout Dates <span className="leise">({liste.length})</span></h2>
          {liste.length === 0 ? (
            <p className="leise wf-leer">Keine. Alle Tage sind frei.</p>
          ) : (
            <ul className="wf-blackout__liste">
              {liste.map((t) => (
                <li key={t.datum}>
                  <button
                    type="button"
                    className={`wf-blackout__eintrag${fokus === t.datum ? " wf-blackout__eintrag--an" : ""}`}
                    onClick={() => {
                      setJahr(Number(t.datum.slice(0, 4)));
                      setFokus(t.datum);
                    }}
                  >
                    <span className="wf-blackout__datum">{t.datum.slice(8, 10)}.{t.datum.slice(5, 7)}.{t.datum.slice(0, 4)}</span>
                    <span className="gekuerzt">{t.name || <span className="leise">ohne Namen</span>}</span>
                    {t.jaehrlich ? <span className="wf-blackout__wdh" title="Jedes Jahr">↻</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
